import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import FileService from '@/services/FileService';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

export interface KYCDocument {
  type: string;
  number: string;
  name?: string;
  file1?: string;
  file2?: string;
}

interface KYCDocumentsTableProps {
  documents: KYCDocument[];
  onChange: (documents: KYCDocument[]) => void;
  sectionTitle: string;
  sectionSubtitle?: string;
  readOnly?: boolean;
}

const DOCUMENT_TYPES = [
  'AADHAR CARD',
  'AUTHORIZATION LETTER',
  'DRIVING LICENCE',
  'ELECTRIC BILL',
  'GST CERTIFICATE',
  'IEC CERTIFICATE',
  'LUT NUMBER',
  'OTHERS',
  'PAN CARD (IF COMPANY, PLEASE PROVIDE COMPANY PAN CARD)',
  'PASSPORT',
  'TELEPHONE BILL',
  'VAT',
  'VOTER ID CARD',
];

const KYCDocumentsTable: React.FC<KYCDocumentsTableProps> = ({
  documents = [],
  onChange,
  sectionTitle,
  sectionSubtitle,
  readOnly = false,
}) => {
  const [localDocs, setLocalDocs] = useState<KYCDocument[]>([]);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  // Initialize checks or merge with existing
  useEffect(() => {
    // Create a map of existing documents for easy lookup
    const existingMap = new Map(documents.map(d => [d.type, d]));

    // Build the full list ensuring all types are present
    const fullList = DOCUMENT_TYPES.map(type => {
      const existing = existingMap.get(type);
      return existing || { type, number: '', name: '', file1: '', file2: '' };
    });

    setLocalDocs(fullList);
  }, [documents]);

  const updateDoc = (updatedDoc: KYCDocument) => {
    const newList = localDocs.map(d =>
      d.type === updatedDoc.type ? updatedDoc : d
    );
    setLocalDocs(newList);

    // Only propagate changes for documents that have some data
    const activeDocs = newList.filter(
      d => d.number || d.name || d.file1 || d.file2
    );
    onChange(activeDocs);
  };

  const handleUpload = async (
    file: File,
    docType: string,
    field: 'file1' | 'file2'
  ) => {
    const key = `${docType}-${field}`;
    setUploading(prev => ({ ...prev, [key]: true }));

    try {
      const result = await FileService.uploadFile(file);

      const currentDoc = localDocs.find(d => d.type === docType);
      if (currentDoc) {
        updateDoc({
          ...currentDoc,
          [field]: result.url, // Storing URL, or could store filePath if backend needs path
        });
      }
    } catch (error) {
      console.error('Upload failed', error);
      // Ideally show toast here
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const openFile = (url?: string) => {
    if (url) window.open(url, '_blank');
  };

  return (
    <div className='space-y-4 border rounded-md p-4 bg-white dark:bg-gray-800'>
      <div className='flex items-center justify-between pb-2 border-b'>
        <h3 className='font-semibold text-lg text-red-600'>{sectionTitle}</h3>
        <span className='text-xs text-muted-foreground uppercase'>
          (
          {sectionSubtitle ||
            `Document uploaded here are save in ${sectionTitle.toLowerCase()} master`}
          )
        </span>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm border-collapse'>
          <thead>
            <tr className='bg-gray-100 dark:bg-gray-700 text-left'>
              <th className='p-2 border font-medium w-1/5'>DOCUMENT TYPE</th>
              <th className='p-2 border font-medium w-32'>DOCUMENT NUMBER</th>
              <th className='p-2 border font-medium w-48'>
                NAME AS PER DOCUMENT
              </th>
              <th className='p-2 border font-medium w-48'>UPLOAD PAGE 1</th>
              <th className='p-2 border font-medium w-48'>UPLOAD PAGE 2</th>
            </tr>
          </thead>
          <tbody>
            {localDocs.map(doc => (
              <tr
                key={doc.type}
                className='border-b hover:bg-gray-50 dark:hover:bg-gray-800/50'
              >
                <td className='p-2 border font-medium'>{doc.type}</td>
                <td className='p-2 border'>
                  <Input
                    disabled={readOnly}
                    value={doc.number}
                    onChange={e =>
                      updateDoc({ ...doc, number: e.target.value })
                    }
                    className='h-8 w-full'
                    placeholder='Number'
                  />
                </td>
                <td className='p-2 border'>
                  <Input
                    disabled={readOnly}
                    value={doc.name || ''}
                    onChange={e => updateDoc({ ...doc, name: e.target.value })}
                    className='h-8 w-full'
                    placeholder='Name'
                  />
                </td>
                <td className='p-2 border'>
                  <div className='flex flex-col gap-1'>
                    {doc.file1 ? (
                      <div className='flex items-center gap-2'>
                        <Button
                          size='sm'
                          variant='destructive'
                          className='h-7 text-xs w-full'
                          onClick={() => openFile(doc.file1)}
                        >
                          VIEW FILE
                        </Button>
                        {!readOnly && (
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-7 w-7 text-red-500'
                            onClick={() => updateDoc({ ...doc, file1: '' })}
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        )}
                      </div>
                    ) : (
                      !readOnly && (
                        <div className='flex items-center gap-2'>
                          <label className='cursor-pointer bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs px-2 py-1 rounded border flex items-center gap-1'>
                            {uploading[`${doc.type}-file1`] ? (
                              <LoadingSpinner size='sm' />
                            ) : (
                              'Choose File'
                            )}
                            <input
                              type='file'
                              className='hidden'
                              onChange={e =>
                                e.target.files?.[0] &&
                                handleUpload(
                                  e.target.files[0],
                                  doc.type,
                                  'file1'
                                )
                              }
                            />
                          </label>
                          <span className='text-xs text-gray-500'>
                            No file chosen
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </td>
                <td className='p-2 border'>
                  <div className='flex flex-col gap-1'>
                    {doc.file2 ? (
                      <div className='flex items-center gap-2'>
                        <Button
                          size='sm'
                          variant='destructive'
                          className='h-7 text-xs w-full'
                          onClick={() => openFile(doc.file2)}
                        >
                          VIEW FILE
                        </Button>
                        {!readOnly && (
                          <Button
                            size='icon'
                            variant='ghost'
                            className='h-7 w-7 text-red-500'
                            onClick={() => updateDoc({ ...doc, file2: '' })}
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        )}
                      </div>
                    ) : (
                      !readOnly && (
                        <div className='flex items-center gap-2'>
                          <label className='cursor-pointer bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs px-2 py-1 rounded border flex items-center gap-1'>
                            {uploading[`${doc.type}-file2`] ? (
                              <LoadingSpinner size='sm' />
                            ) : (
                              'Choose File'
                            )}
                            <input
                              type='file'
                              className='hidden'
                              onChange={e =>
                                e.target.files?.[0] &&
                                handleUpload(
                                  e.target.files[0],
                                  doc.type,
                                  'file2'
                                )
                              }
                            />
                          </label>
                          <span className='text-xs text-gray-500'>
                            No file chosen
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KYCDocumentsTable;
